import type { MessageKey } from '@dijitalapp/i18n';
import {
  NOTIFICATION_PREVIEWS,
  type Audience,
  type DeviceDto,
  type NotificationPreview,
  type PasskeyDto,
  type PrivacyDto,
  type PrivacySettings,
} from '@dijitalapp/shared';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Icon } from '../components/Icon';
import { api, errorMessageKey } from '../lib/api';
import { useApp, type ThemePreference } from '../lib/app-context';
import { PasskeyCancelledError, passkeysSupported, registerPasskey, suggestPasskeyName } from '../lib/passkeys';
import { disablePush, enablePush, pushState, type PushState } from '../lib/push';

const BOOLEAN_SETTINGS: { key: keyof PrivacySettings; label: MessageKey }[] = [
  { key: 'profileDiscoverable', label: 'privacy.profileDiscoverable' },
  { key: 'dmFromNonContacts', label: 'privacy.dmFromNonContacts' },
  { key: 'phoneVisible', label: 'privacy.phoneVisible' },
  { key: 'readReceipts', label: 'privacy.readReceipts' },
];
const AUDIENCES: Audience[] = ['everyone', 'contacts', 'nobody'];

function PrivacySection() {
  const { i18n } = useApp();
  const { t } = i18n;
  const [data, setData] = useState<PrivacyDto | null>(null);
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    void api<PrivacyDto>('GET', '/me/privacy').then(setData);
  }, []);

  const update = async (patch: Partial<PrivacySettings>) => {
    try {
      setData(await api<PrivacyDto>('PATCH', '/me/privacy', patch));
      setStatus({ kind: 'ok', text: t('privacy.saved') });
    } catch (err) {
      setStatus({ kind: 'error', text: t(errorMessageKey(err)) });
    }
  };

  if (!data) return <p className="muted">{t('app.loading')}</p>;
  return (
    <section className="card" aria-labelledby="privacy-title">
      <h2 id="privacy-title">{t('settings.privacy')}</h2>
      {BOOLEAN_SETTINGS.map(({ key, label }) => {
        const locked = data.locked.includes(key);
        return (
          <label key={key} className="toggle-row">
            <span>
              {t(label)}
              {locked && (
                <span className="hint" style={{ display: 'block' }}>
                  {t('privacy.lockedByAge')}
                </span>
              )}
            </span>
            <span className="spacer" />
            <input
              type="checkbox"
              role="switch"
              checked={Boolean(data.settings[key])}
              disabled={locked}
              onChange={(e) => void update({ [key]: e.target.checked })}
              style={{ width: 22, height: 22 }}
            />
          </label>
        );
      })}
      <div className="toggle-row">
        <label htmlFor="group-add">{t('privacy.groupAdd')}</label>
        <span className="spacer" />
        <select
          id="group-add"
          className="input"
          style={{ width: 'auto' }}
          value={data.settings.groupAdd}
          onChange={(e) => void update({ groupAdd: e.target.value as Audience })}
        >
          {AUDIENCES.filter((a) => a !== 'everyone' || !data.locked.includes('profileDiscoverable')).map((a) => (
            <option key={a} value={a}>
              {t(`privacy.audience.${a}` as MessageKey)}
            </option>
          ))}
        </select>
      </div>
      <div className="toggle-row">
        <label htmlFor="last-seen">{t('privacy.lastSeen')}</label>
        <span className="spacer" />
        <select
          id="last-seen"
          className="input"
          style={{ width: 'auto' }}
          value={data.settings.lastSeen}
          onChange={(e) => void update({ lastSeen: e.target.value as Audience })}
        >
          {AUDIENCES.map((a) => (
            <option key={a} value={a}>
              {t(`privacy.audience.${a}` as MessageKey)}
            </option>
          ))}
        </select>
      </div>
      {status && (
        <p role="status" className={status.kind === 'error' ? 'alert alert-error' : 'muted'}>
          {status.text}
        </p>
      )}
    </section>
  );
}

function DevicesSection() {
  const { i18n, setMe } = useApp();
  const { t, formatDate } = i18n;
  const navigate = useNavigate();
  const [devices, setDevices] = useState<DeviceDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchDevices = () => api<{ devices: DeviceDto[] }>('GET', '/devices').then((r) => r.devices);
  const load = async () => setDevices(await fetchDevices());
  useEffect(() => {
    void fetchDevices().then(setDevices);
  }, []);

  const act = async (fn: () => Promise<unknown>, signedOutSelf = false) => {
    try {
      await fn();
      if (signedOutSelf) {
        setMe(null);
        navigate('/login', { replace: true });
      } else await load();
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  if (!devices) return <p className="muted">{t('app.loading')}</p>;
  return (
    <section className="card stack" aria-labelledby="devices-title">
      <div className="row">
        <h2 id="devices-title">{t('settings.devices')}</h2>
        <span className="badge">{t('devices.count', { count: devices.length })}</span>
      </div>
      {error && (
        <div className="alert alert-error" role="alert">
          {error}
        </div>
      )}
      {devices.map((d) => (
        <div key={d.id} className="toggle-row">
          <div style={{ minWidth: 0 }}>
            <strong>{d.name}</strong> {d.current && <span className="badge">{t('devices.current')}</span>}
            <p className="muted">{t('devices.lastActive', { date: formatDate(d.lastActiveAt) })}</p>
            <p className="muted">{t('devices.firstSeen', { date: formatDate(d.firstSeenAt) })}</p>
          </div>
          <span className="spacer" />
          <button className="btn btn-danger" type="button" onClick={() => void act(() => api('DELETE', `/devices/${d.id}`), d.current)}>
            {t('devices.signOut')}
          </button>
        </div>
      ))}
      {devices.length > 1 && (
        <button className="btn btn-danger" type="button" onClick={() => void act(() => api('POST', '/devices/revoke-others'))}>
          {t('devices.signOutOthers')}
        </button>
      )}
    </section>
  );
}

const PUSH_STATE_TEXT: Record<Exclude<PushState, 'off'>, MessageKey> = {
  on: 'notifications.enabled',
  denied: 'notifications.denied',
  unsupported: 'notifications.unsupported',
  unavailable: 'notifications.unavailable',
};

function NotificationsSection() {
  const { i18n } = useApp();
  const { t } = i18n;
  const [state, setState] = useState<PushState | null>(null);
  const [preview, setPreview] = useState<NotificationPreview>('sender');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void pushState().then(setState, () => setState('unavailable'));
    void api<{ preview: NotificationPreview }>('GET', '/me/notifications').then((r) => setPreview(r.preview));
  }, []);

  const run = async (fn: () => Promise<PushState>) => {
    setError(null);
    try {
      setState(await fn());
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  const changePreview = async (value: NotificationPreview) => {
    setPreview(value);
    try {
      await api('PATCH', '/me/notifications', { preview: value });
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  return (
    <section className="card stack" aria-labelledby="notifications-title">
      <h2 id="notifications-title">{t('notifications.title')}</h2>
      <p className="muted">{t('notifications.body')}</p>
      {state && state !== 'off' && <p role="status">{t(PUSH_STATE_TEXT[state])}</p>}
      {state === 'off' && (
        <button className="btn btn-primary" type="button" onClick={() => void run(enablePush)}>
          {t('notifications.enable')}
        </button>
      )}
      {state === 'on' && (
        <button className="btn" type="button" onClick={() => void run(disablePush)}>
          {t('notifications.disable')}
        </button>
      )}
      <div className="field">
        <label htmlFor="notification-preview">{t('notifications.preview')}</label>
        <select
          id="notification-preview"
          className="input"
          value={preview}
          aria-describedby="notification-preview-hint"
          onChange={(e) => void changePreview(e.target.value as NotificationPreview)}
        >
          {NOTIFICATION_PREVIEWS.map((p) => (
            <option key={p} value={p}>
              {t(`notifications.preview.${p}` as MessageKey)}
            </option>
          ))}
        </select>
        <span className="hint" id="notification-preview-hint">
          {t('notifications.previewHint')}
        </span>
      </div>
      {error && (
        <p className="alert alert-error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

const fetchPasskeys = () => api<{ passkeys: PasskeyDto[] }>('GET', '/passkeys').then((r) => r.passkeys);

function PasskeysSection() {
  const { i18n, config } = useApp();
  const { t, formatDate } = i18n;
  const [passkeys, setPasskeys] = useState<PasskeyDto[] | null>(null);
  const [supported, setSupported] = useState(false);
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    void fetchPasskeys().then(setPasskeys);
    void passkeysSupported().then(setSupported, () => setSupported(false));
  }, []);

  const run = async (fn: () => Promise<unknown>, okText?: string) => {
    setStatus(null);
    try {
      await fn();
      setPasskeys(await fetchPasskeys());
      if (okText) setStatus({ kind: 'ok', text: okText });
    } catch (err) {
      const text = err instanceof PasskeyCancelledError ? t('auth.passkey.cancelled') : t(errorMessageKey(err));
      setStatus({ kind: 'error', text });
    }
  };

  if (!config?.authMethods.passkey) return null;
  return (
    <section className="card stack" aria-labelledby="passkeys-title">
      <h2 id="passkeys-title">{t('passkeys.title')}</h2>
      <p className="muted">{t('passkeys.body')}</p>
      {passkeys?.length === 0 && <p className="muted">{t('passkeys.empty')}</p>}
      {passkeys?.map((p) => (
        <div key={p.id} className="toggle-row">
          <div style={{ minWidth: 0 }}>
            <strong>{p.name}</strong> {p.deviceType === 'multiDevice' && <span className="badge">{t('passkeys.synced')}</span>}
            <p className="muted">{t('passkeys.created', { date: formatDate(p.createdAt) })}</p>
            {p.lastUsedAt && <p className="muted">{t('passkeys.lastUsed', { date: formatDate(p.lastUsedAt) })}</p>}
          </div>
          <span className="spacer" />
          <button className="btn btn-danger" type="button" onClick={() => void run(() => api('DELETE', `/passkeys/${p.id}`))}>
            {t('passkeys.remove')}
          </button>
        </div>
      ))}
      {supported ? (
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => void run(() => registerPasskey(suggestPasskeyName()), t('passkeys.added'))}
        >
          {t('passkeys.add')}
        </button>
      ) : (
        <p className="hint">{t('auth.passkey.unsupported')}</p>
      )}
      {status && (
        <p role="status" className={status.kind === 'error' ? 'alert alert-error' : 'muted'}>
          {status.text}
        </p>
      )}
    </section>
  );
}

export function SettingsPage() {
  const { i18n, theme, setTheme, setLocale, setMe } = useApp();
  const { t } = i18n;
  const navigate = useNavigate();

  const logout = async () => {
    await api('POST', '/auth/logout').catch(() => undefined);
    setMe(null);
    navigate('/login', { replace: true });
  };

  return (
    <div className="content-inner">
      <div className="row">
        <h1>{t('settings.title')}</h1>
        <span className="spacer" />
        <button className="btn" type="button" onClick={() => void logout()}>
          {t('auth.logout')}
        </button>
      </div>

      <section className="card stack" aria-labelledby="appearance-title">
        <h2 id="appearance-title">{t('settings.appearance')}</h2>
        <div className="row" role="radiogroup" aria-label={t('settings.appearance')}>
          {(['light', 'dark', 'system'] as ThemePreference[]).map((th) => (
            <button key={th} type="button" className="btn" role="radio" aria-checked={theme === th} onClick={() => setTheme(th)}>
              {t(`settings.theme.${th}` as MessageKey)}
            </button>
          ))}
        </div>
        <div className="field">
          <label htmlFor="lang">{t('settings.language')}</label>
          <select id="lang" className="input" value={i18n.locale} onChange={(e) => setLocale(e.target.value as 'tr' | 'en')}>
            <option value="tr">Türkçe</option>
            <option value="en">English</option>
          </select>
        </div>
      </section>

      <PrivacySection />
      <NotificationsSection />
      <PasskeysSection />
      <DevicesSection />
      <p className="muted row">
        <Icon name="lock" /> DijitalApp
      </p>
    </div>
  );
}
